// src/Payroll_Management/PayRoll_Report/SalaryHoldList.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  FileSpreadsheet,
  Hash,
  Type,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const MONTHS = [
  { value: "Select", text: "Select" },
  { value: "1", text: "January" },
  { value: "2", text: "February" },
  { value: "3", text: "March" },
  { value: "4", text: "April" },
  { value: "5", text: "May" },
  { value: "6", text: "June" },
  { value: "7", text: "July" },
  { value: "8", text: "August" },
  { value: "9", text: "September" },
  { value: "10", text: "October" },
  { value: "11", text: "November" },
  { value: "12", text: "December" },
];

const SalaryHoldList = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // session-ish values used everywhere in your project
  const finYearId = localStorage.getItem("finYearId") || "";
  const branchIdLS = localStorage.getItem("branchId") || "";
  const branchTypeLS = localStorage.getItem("branchType") || "HO"; // "HO" or others

  // meta
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);

  // filters
  const [year, setYear] = useState("Select");
  const [monthValue, setMonthValue] = useState("Select"); // 1..12
  const [branchId, setBranchId] = useState(branchIdLS ? Number(branchIdLS) : 0);
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  // autocomplete
  const [empCodeSug, setEmpCodeSug] = useState([]);
  const [empNameSug, setEmpNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination (same style as your template)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-49", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const monthName = useMemo(() => {
    const m = MONTHS.find((x) => x.value === String(monthValue));
    return m?.text || "";
  }, [monthValue]);

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!branchIdLS || !branchTypeLS) {
      toast.warn("branchId / branchType missing in localStorage.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/salary-hold-list/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchIdLS),
          branchType: String(branchTypeLS),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setYears(res.data.years || []);

        const defBranch = Number(res.data.defaultBranchId || 0);
        const defYear = String(res.data.defaultYear || "Select");
        const defMonth = String(res.data.defaultMonth || "Select");

        setBranchId(defBranch);
        setYear(defYear);
        setMonthValue(defMonth);

        // load initial list (ASP loads on first load)
        setPage(1);
        await fetchList(1, {
          branchId: defBranch,
          year: defYear,
          monthValue: defMonth,
          empName: "",
          empCode: "",
        });
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page, override = null) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    const effective = {
      branchId,
      year,
      monthValue,
      empName,
      empCode,
      ...(override || {}),
    };

    // ASP had * 표시 on Year/Month/Branch, but didn’t hard-block.
    // In React we enforce mandatory to avoid SP garbage.
    if (!effective.branchId) {
      toast.warn("Please select Branch");
      return;
    }
    if (!effective.year || effective.year === "Select") {
      toast.warn("Please select Year");
      return;
    }
    if (!effective.monthValue || effective.monthValue === "Select") {
      toast.warn("Please select Month");
      return;
    }

    const monthText = MONTHS.find((m) => m.value === String(effective.monthValue))?.text || "";
    if (!monthText || monthText === "Select") {
      toast.warn("Please select Month");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/salary-hold-list/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(effective.branchId),
          branchType: String(branchTypeLS),
          monthName: monthText, // ✅ SP wants month text
          year: String(effective.year),
          empName: String(effective.empName || ""),
          empCode: String(effective.empCode || ""),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load salary hold list.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // like your template: page change triggers fetch
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchList(page);
  };

  // ------------ Autocomplete helpers (simple, no external libs) ------------
  const fetchEmpCodeSug = async (q) => {
    if (!q.trim() || q.trim().length < 1) {
      setEmpCodeSug([]);
      return;
    }
    if (!branchId) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/salary-hold-list/autocomplete/emp-code`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchTypeLS),
          q: q.trim(),
        },
      });
      if (res?.data?.success) setEmpCodeSug(res.data.items || []);
    } catch {
      // silent (autocomplete should not spam toasts)
    }
  };

  const fetchEmpNameSug = async (q) => {
    if (!q.trim() || q.trim().length < 1) {
      setEmpNameSug([]);
      return;
    }
    if (!branchId) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/salary-hold-list/autocomplete/emp-name`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchTypeLS),
          q: q.trim(),
        },
      });
      if (res?.data?.success) setEmpNameSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  // tiny debounce without extra libs
  useEffect(() => {
    const t = setTimeout(() => {
      if (showCodeSug) fetchEmpCodeSug(empCode);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (showNameSug) fetchEmpNameSug(empName);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName]);

  // ------------ Print / Export ------------
  const handleRowPrint = (empId) => {
    // ASP: sets session HoldEmpID and opens SalaryHoldListPrint.aspx
    // Here: we open a React print route (you will create it later)
    const url = `/PayRoll/SalaryHoldEmployeePrint?empId=${empId}&branchId=${branchId}&month=${encodeURIComponent(
      monthName
    )}&year=${encodeURIComponent(year)}`;
    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  };

  const handlePrintAll = async () => {
    if (rows.length === 0) {
      toast.warn("No Records Found");
      return;
    }
    // Open a print route; that route can call /print-data for full dataset.
    const url = `/PayRoll/SalaryHoldList_Print?branchId=${branchId}&month=${encodeURIComponent(
      monthName
    )}&year=${encodeURIComponent(year)}&empName=${encodeURIComponent(empName || "")}&empCode=${encodeURIComponent(
      empCode || ""
    )}`;
    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  };

  const handleExcel = () => {
    // only if you implement /export-excel endpoint
    if (!rows.length) {
      toast.warn("No Records Found");
      return;
    }
    const qs =
      `finYearId=${Number(finYearId)}` +
      `&branchId=${Number(branchId)}` +
      `&monthName=${encodeURIComponent(monthName)}` +
      `&year=${encodeURIComponent(year)}` +
      `&empName=${encodeURIComponent(empName || "")}` +
      `&empCode=${encodeURIComponent(empCode || "")}`;

    window.location.href = `${BASE_URL}/api/salary-hold-list/export-excel?${qs}`;
  };

  const MAX_PAGE_BTNS = 5;

const getPageWindow = (current, total, maxBtns) => {
  if (total <= maxBtns) {
    return { start: 1, end: total };
  }

  const half = Math.floor(maxBtns / 2);
  let start = current - half;
  let end = current + half;

  // If maxBtns is even, keep window length correct
  if (maxBtns % 2 === 0) end -= 1;

  if (start < 1) {
    start = 1;
    end = maxBtns;
  }
  if (end > total) {
    end = total;
    start = total - maxBtns + 1;
  }
  return { start, end };
};

const { start: pageStart, end: pageEnd } = getPageWindow(page, totalPages, MAX_PAGE_BTNS);
const visiblePages = Array.from({ length: pageEnd - pageStart + 1 }, (_, i) => pageStart + i);


  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALARY HOLD LIST</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Salary Hold List
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Print / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrintAll} className={primaryBtn} disabled={loading || total === 0}>
                <Printer className="w-4 h-4" />
                Print List
              </button>

              {/* <button onClick={handleExcel} className={ghostBtn} disabled={loading || total === 0}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button> */}
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {/* Year */}
              <div>
                <label className={labelBase}>
                  Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className={`${inputBase} mt-2`}
                >
                  {(years || []).map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.text}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>
                  Month <span className="text-red-500">*</span>
                </label>
                <select
                  value={monthValue}
                  onChange={(e) => setMonthValue(e.target.value)}
                  className={`${inputBase} mt-2`}
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.text}
                    </option>
                  ))}
                </select>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>
                  Branch <span className="text-red-500">*</span>
                </label>
                <select
                  value={branchId || 0}
                  onChange={(e) => setBranchId(Number(e.target.value))}
                  className={`${inputBase} mt-2`}
                >
                  {(branches || []).map((b, idx) => (
                    <option key={idx} value={Number(b.fld_branchid)}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Emp Code autocomplete */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empCode}
                    onChange={(e) => {
                      setEmpCode(e.target.value);
                      setShowCodeSug(true);
                    }}
                    onBlur={() => setTimeout(() => setShowCodeSug(false), 120)}
                    onFocus={() => setShowCodeSug(true)}
                    placeholder="Type employee code..."
                    className={inputBase}
                  />
                </div>

                {showCodeSug && empCodeSug.length > 0 && (
                  <div
                    className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg max-h-56 overflow-auto ${
                      isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                    }`}
                  >
                    {empCodeSug.map((it, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onMouseDown={() => {
                          setEmpCode(String(it));
                          setShowCodeSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:${
                          isDark ? "bg-white/10" : "bg-gray-50"
                        } ${isDark ? "text-gray-200" : "text-gray-800"}`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Emp Name autocomplete */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empName}
                    onChange={(e) => {
                      setEmpName(e.target.value);
                      setShowNameSug(true);
                    }}
                    onBlur={() => setTimeout(() => setShowNameSug(false), 120)}
                    onFocus={() => setShowNameSug(true)}
                    placeholder="Type employee name..."
                    className={inputBase}
                  />
                </div>

                {showNameSug && empNameSug.length > 0 && (
                  <div
                    className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg max-h-56 overflow-auto ${
                      isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                    }`}
                  >
                    {empNameSug.map((it, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onMouseDown={() => {
                          setEmpName(String(it));
                          setShowNameSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:${
                          isDark ? "bg-white/10" : "bg-gray-50"
                        } ${isDark ? "text-gray-200" : "text-gray-800"}`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Print</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 4 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.fld_employeeid}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleRowPrint(r.fld_employeeid)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                          >
                            <Printer className="w-4 h-4" />
                            Print
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

           {totalPages > 1 && (
  <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
    {/* First */}
    <button
      onClick={() => setPage(1)}
      disabled={page === 1}
      className={`px-4 py-2 text-sm font-bold ${
        page === 1
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
    >
      First
    </button>

    {/* Prev */}
    <button
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      disabled={page === 1}
      className={`px-4 py-2 text-sm font-bold ${
        page === 1
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
    >
      Prev
    </button>

    {/* Jump back window */}
    {pageStart > 1 && (
      <button
        onClick={() => setPage(pageStart - 1)}
        className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        title="Previous pages"
      >
        …
      </button>
    )}

    {/* Page numbers (only 5) */}
    {visiblePages.map((pno) => (
      <button
        key={pno}
        onClick={() => setPage(pno)}
        className={`px-4 py-2 text-sm font-bold transition ${
          page === pno
            ? `${primaryBtnClass} text-white`
            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        }`}
      >
        {pno}
      </button>
    ))}

    {/* Jump forward window */}
    {pageEnd < totalPages && (
      <button
        onClick={() => setPage(pageEnd + 1)}
        className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        title="Next pages"
      >
        …
      </button>
    )}

    {/* Next */}
    <button
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      disabled={page === totalPages}
      className={`px-4 py-2 text-sm font-bold ${
        page === totalPages
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
    >
      Next
    </button>

    {/* Last */}
    <button
      onClick={() => setPage(totalPages)}
      disabled={page === totalPages}
      className={`px-4 py-2 text-sm font-bold ${
        page === totalPages
          ? "text-gray-400 cursor-not-allowed"
          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
      }`}
    >
      Last
    </button>
  </div>
)}

          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SalaryHoldList;
